// SPDX-License-Identifier: BUSL-1.1

/**
 * Session table column definitions for the agent-detail Sessions tab.
 *
 * Extracted from `routes/agent-detail.tsx` during the P3b refactor.
 */

import { type ColumnDef } from '@tanstack/react-table';
import type { Session } from '@/types/api';
import { SESSION_STATUS_COLORS } from '@/types/api';
import { estimateCost, formatDuration, getSessionDuration } from './helpers';

export const sessionColumns: ColumnDef<Session>[] = [
  {
    accessorKey: 'id',
    header: 'Session ID',
    cell: ({ row }) => (
      <span className="font-mono text-xs text-neutral-300">
        {row.original.id.slice(0, 12)}...
      </span>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.status;
      const colors =
        SESSION_STATUS_COLORS[status] ?? SESSION_STATUS_COLORS.terminated;
      return (
        <span className={`inline-flex rounded px-2 py-0.5 text-xs ${colors}`}>
          {status}
        </span>
      );
    },
  },
  {
    id: 'cost',
    header: 'Cost',
    cell: ({ row }) => (
      <span className="text-neutral-300">{estimateCost(row.original)}</span>
    ),
  },
  {
    id: 'duration',
    header: 'Duration',
    cell: ({ row }) => {
      const duration = getSessionDuration(row.original);
      // Show human-readable duration if available (from API)
      if (row.original.durationHuman) {
        return <span className="text-neutral-400">{row.original.durationHuman}</span>;
      }
      return <span className="text-neutral-400">{formatDuration(duration)}</span>;
    },
  },
  {
    accessorKey: 'title',
    header: 'Title',
    cell: ({ row }) => (
      <span className="text-neutral-400">{row.original.title ?? '-'}</span>
    ),
  },
  {
    accessorKey: 'createdAt',
    header: 'Created',
    cell: ({ row }) => (
      <span className="text-neutral-400">
        {new Date(row.original.createdAt).toLocaleString()}
      </span>
    ),
  },
];
